/**
 * Display-set rules: how one DICOM series splits into the stacks shown
 * separately, and what each part is called.
 *
 * THIS FILE IS SHARED VERBATIM between pacsbin-v2
 * (`shared/utils/display-set-rules.ts`) and pacsbin-uploader
 * (`src/display-set-rules.ts`), so the split the uploader makes and the split
 * the viewer offers can never disagree. Edit one, then copy it over the other.
 * v2's `display-sets.test.ts` fails while the two differ, when both repos are
 * checked out side by side. Keep it pure and free of imports.
 *
 * A series is not a display unit. Ultrasound routinely puts 2D stills, Doppler,
 * cine loops and report screens under one SeriesInstanceUID, and scanners of
 * other modalities append report or projection frames at a different size.
 * Stacking a clip against unrelated stills makes scroll, cine and
 * fit-to-window meaningless. The rules:
 *
 *  1. Every clip is its own part — the frames of one loop are the only
 *     genuinely stackable thing among them. Whether a multiframe instance is a
 *     clip or a volume is asked of the metadata (`classifyMultiframe`).
 *  2. Everything else groups by matrix.
 *
 * Each part gets a base label — "clip", "stills 800×600", "images" — and that
 * free text is all pacsbin stores about a part. Numbering ("clip 2 of 3") is
 * added at display time by `numberDisplaySetLabels`, so it stays right when a
 * part drops out. Because labels are free text, a future rule (DWI b-values,
 * echoes, cardiac phases) only has to write a different label: nothing that
 * stores or shows a part needs to know which rule made it.
 */

/**
 * The headers detection needs, one entry per instance in series order.
 *
 * Everything past `modality` answers one question for a multiframe instance:
 * do its frames advance in time (a clip) or in space (a stack)? See
 * `classifyMultiframe`. All are optional — a caller supplies what its source
 * carries, and detection falls back to modality when none of it is there.
 */
export interface DisplaySetHeaders {
  instanceId: string;
  rows?: unknown;
  columns?: unknown;
  numberOfFrames?: unknown;
  modality?: unknown;
  /**
   * (0028,0009) FrameIncrementPointer: the tag(s) that vary frame to frame.
   * DICOM JSON gives AT values as hex strings (`"00181063"`); dcmjs gives them
   * as numbers (`0x00181063`). Either is accepted. Mandatory in a classic
   * multiframe instance, and the standard's own answer to this question.
   */
  frameIncrementPointer?: unknown;
  /**
   * Enhanced multiframe instances have no FrameIncrementPointer; their frames
   * carry an ImagePositionPatient each. True when every frame sits on the same
   * plane (so only time can be advancing), false when the planes differ (a
   * volume), undefined when there's nothing to compare. See
   * `framesShareOnePlaneFromDicomJson`.
   */
  framesShareOnePlane?: boolean;
  /**
   * Cine module timing — (0018,1063) FrameTime, (0018,1065) FrameTimeVector,
   * (0018,0040) CineRate, (0008,2144) RecommendedDisplayFrameRate,
   * (0018,1242) ActualFrameDuration. A playback rate implies something worth
   * playing, but it is the weakest signal here: NM tomography also times its
   * frames, which is why the two above are consulted first.
   */
  frameTime?: unknown;
  frameTimeVector?: unknown;
  cineRate?: unknown;
  recommendedDisplayFrameRate?: unknown;
  actualFrameDuration?: unknown;
}

export interface DetectedDisplaySets {
  /** Each part's base label, in order of first appearance. */
  labels: string[];
  /**
   * A stable key per part (`cine:<instanceId>`, `still:<matrix>`), for callers
   * that need an identity that survives re-running detection.
   */
  keys: string[];
  /** Instance id -> 1-based part number. */
  assignment: Record<string, number>;
  /** What the split would produce, e.g. "2 clips and stills in 2 sizes". */
  summary: string;
}

function positiveInt(value: unknown): number | null {
  if (value == null) return null;
  const parsed = parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * Whether a multiframe instance's frames advance in time (a clip) or in space
 * (a stack), asked of the metadata rather than of the modality.
 *
 * Modality is a poor proxy in both directions: an enhanced CT or MR object is
 * usually a volume, but cardiac MR cines, dynamic NM and gated studies are
 * genuine loops, while a multiframe MG is tomosynthesis. What actually
 * distinguishes them is stated in the data:
 *
 *  - Classic multiframe (US, XA, NM, ...) must carry FrameIncrementPointer
 *    (0028,0009), naming the attribute that varies between frames. Pointing at
 *    FrameTime or FrameTimeVector — or, in NM, at a phase/time-slot/RR-interval
 *    vector — means time. Pointing at a slice, rotation or angular-view vector
 *    means space. Verified against real files: an XA cine and a US cine point
 *    at (0018,1063); a variable-rate US cine points at (0018,1065).
 *  - Enhanced multiframe has no such pointer; each frame carries its own
 *    ImagePositionPatient in the per-frame functional groups (or one shared
 *    position for all of them). Frames that share a plane can only be
 *    advancing in time; frames that march along the normal are a volume. An
 *    enhanced CT sample has a PlanePositionSequence per frame with distinct
 *    positions, which is exactly the volume case.
 *  - Failing both, a declared playback rate at least says someone meant this
 *    to be played.
 *
 * Returns "unknown" when the metadata says none of it — a real possibility,
 * since a DICOMweb server may serve a reduced tag set — and the caller then
 * falls back to modality.
 */
export type MultiframeKind = "cine" | "stack" | "unknown";

/** Frame-to-frame variations that are temporal, as DICOM JSON tag keys. */
const TEMPORAL_FRAME_POINTERS = new Set([
  "00181063", // FrameTime
  "00181065", // FrameTimeVector
  "00540030", // PhaseVector (NM, dynamic)
  "00540060", // RRIntervalVector (NM, gated)
  "00540070", // TimeSlotVector (NM, gated)
  "00540100", // TimeSliceVector (NM, dynamic)
]);

/** AT values, one or many, as bare uppercase tag keys. */
function tagKeys(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((entry) => {
    if (typeof entry === "number" && Number.isInteger(entry) && entry >= 0) {
      return [entry.toString(16).toUpperCase().padStart(8, "0")];
    }
    if (typeof entry !== "string") return [];
    const key = entry.replace(/[^0-9a-fA-F]/g, "").toUpperCase();
    return key.length === 8 ? [key] : [];
  });
}

function hasPlaybackRate(header: DisplaySetHeaders): boolean {
  return [
    header.frameTime,
    header.frameTimeVector,
    header.cineRate,
    header.recommendedDisplayFrameRate,
    header.actualFrameDuration,
  ].some((value) => {
    if (value == null || value === "") return false;
    const first = Array.isArray(value) ? value[0] : value;
    return (Number(first) || 0) > 0;
  });
}

/**
 * Read `framesShareOnePlane` out of an instance's DICOM JSON — a DICOMweb
 * `/metadata` entry or a dcmjs dict, which share the `{ Value }` shape.
 *
 * One ImagePositionPatient shared by every frame means only time can be
 * advancing — a cine. Distinct per-frame positions mean a volume. Undefined
 * when the functional groups carry no position, which is also what a classic
 * (non-enhanced) multiframe instance looks like: those answer the question
 * with FrameIncrementPointer instead.
 */
export function framesShareOnePlaneFromDicomJson(
  instance: Record<string, any> | undefined | null
): boolean | undefined {
  const SHARED_FUNCTIONAL_GROUPS = "52009229";
  const PER_FRAME_FUNCTIONAL_GROUPS = "52009230";
  const PLANE_POSITION_SEQUENCE = "00209113";
  const IMAGE_POSITION_PATIENT = "00200032";

  const positionIn = (group: any): unknown[] | undefined =>
    group?.[PLANE_POSITION_SEQUENCE]?.Value?.[0]?.[IMAGE_POSITION_PATIENT]
      ?.Value;

  const shared = instance?.[SHARED_FUNCTIONAL_GROUPS]?.Value?.[0];
  if (positionIn(shared)) return true;

  const perFrame = instance?.[PER_FRAME_FUNCTIONAL_GROUPS]?.Value;
  if (!Array.isArray(perFrame) || perFrame.length < 2) return undefined;

  const positions = perFrame.map((frame) => positionIn(frame)?.join("\\"));
  if (positions.some((position) => position === undefined)) return undefined;
  return new Set(positions).size === 1;
}

export function classifyMultiframe(header: DisplaySetHeaders): MultiframeKind {
  if ((positiveInt(header.numberOfFrames) ?? 1) <= 1) return "stack";

  const pointers = tagKeys(header.frameIncrementPointer);
  if (pointers.length > 0) {
    return pointers.some((tag) => TEMPORAL_FRAME_POINTERS.has(tag))
      ? "cine"
      : "stack";
  }

  if (header.framesShareOnePlane === true) return "cine";
  if (header.framesShareOnePlane === false) return "stack";

  return hasPlaybackRate(header) ? "cine" : "unknown";
}

/**
 * Modalities assumed volumetric when the metadata gives nothing to go on (see
 * classifyMultiframe). A fallback, not a rule: these are the modalities whose
 * multiframe objects are volumes often enough that guessing "stack" beats
 * guessing "clip".
 */
const VOLUMETRIC_MULTIFRAME_MODALITIES = new Set(["CT", "MR", "PT"]);

/** True when a multiframe instance of this modality is assumed to be a stack. */
export function isVolumetricModality(modality: unknown): boolean {
  return (
    typeof modality === "string" &&
    VOLUMETRIC_MULTIFRAME_MODALITIES.has(modality.trim().toUpperCase())
  );
}

/** Whether this instance is a clip: its own display-set part, playable as one. */
export function isCineInstance(header: DisplaySetHeaders): boolean {
  const kind = classifyMultiframe(header);
  if (kind !== "unknown") return kind === "cine";
  return (
    (positiveInt(header.numberOfFrames) ?? 1) > 1 &&
    !isVolumetricModality(header.modality)
  );
}

/** An image matrix as `"<columns>x<rows>"`, or null if either is unknown. */
export function matrixKey(rows: unknown, columns: unknown): string | null {
  const r = positiveInt(rows);
  const c = positiveInt(columns);
  return r && c ? `${c}x${r}` : null;
}

/** `"800x600"` -> `"800×600"`, for display. */
export function formatMatrixKey(key: string): string {
  return key.replace("x", "×");
}

/**
 * What to call a part that isn't a clip. "stills" is right for the modalities
 * that mix loops and single images; CT/MR/PET parts are slices of an
 * acquisition, so they read as plain images.
 */
function imagesWord(modality: unknown): string {
  return isVolumetricModality(modality) ? "images" : "stills";
}

/**
 * Group a series' instances into parts, preserving instance order and ordering
 * parts by first appearance. Returns null when everything lands in one part —
 * the common case, where there is nothing to split.
 */
export function detectDisplaySets(
  headers: DisplaySetHeaders[]
): DetectedDisplaySets | null {
  const partByKey = new Map<string, number>();
  const parts: {
    key: string;
    cine: boolean;
    matrix: string | null;
    modality: unknown;
  }[] = [];
  const assignment: Record<string, number> = {};

  for (const header of headers) {
    const cine = isCineInstance(header);
    const matrix = matrixKey(header.rows, header.columns);
    const key = cine
      ? `cine:${header.instanceId}`
      : `still:${matrix ?? "unknown"}`;

    let partNumber = partByKey.get(key);
    if (partNumber === undefined) {
      parts.push({ key, cine, matrix, modality: header.modality });
      partNumber = parts.length;
      partByKey.set(key, partNumber);
    }
    assignment[header.instanceId] = partNumber;
  }

  if (parts.length < 2) return null;

  // A size only earns its place in a label when it tells two parts apart.
  const imageParts = parts.filter((part) => !part.cine).length;
  const labels = parts.map((part) => {
    if (part.cine) return "clip";
    const word = imagesWord(part.modality);
    return imageParts > 1 && part.matrix
      ? `${word} ${formatMatrixKey(part.matrix)}`
      : word;
  });

  const clips = parts.length - imageParts;
  const word = imagesWord(headers[0]?.modality);
  const pieces: string[] = [];
  if (clips) pieces.push(clips === 1 ? "1 clip" : `${clips} clips`);
  if (imageParts) {
    pieces.push(imageParts === 1 ? word : `${word} in ${imageParts} sizes`);
  }

  return {
    labels,
    keys: parts.map((part) => part.key),
    assignment,
    summary: pieces.join(" and "),
  };
}

/**
 * The labels to show for a series' parts: a base label shared by several parts
 * gets numbered ("clip" -> "clip 1 of 2", "clip 2 of 2"); a unique one is shown
 * as is. Pass only the parts being shown, so the numbering skips any that
 * dropped out.
 */
export function numberDisplaySetLabels(labels: string[]): string[] {
  const total = new Map<string, number>();
  for (const label of labels) total.set(label, (total.get(label) ?? 0) + 1);
  const seen = new Map<string, number>();
  return labels.map((label) => {
    const count = total.get(label)!;
    if (count === 1) return label;
    const index = (seen.get(label) ?? 0) + 1;
    seen.set(label, index);
    return `${label} ${index} of ${count}`;
  });
}
